import React from 'react';
import './PricingSheetPage.css';
import APP_CONFIG, {
    TIER_ORDER,
    tierAvailability,
    getPlatformGroups,
    getCoreFeatures,
    getOptionalFeatures,
    formatCapacity
} from '../config/appConfig';
import Logo from '../components/Logo';

/**
 * The published pricing sheet.
 *
 * Rendered from the same APP_CONFIG that prices every quote, deliberately: a
 * printed sheet that disagrees with the calculator is worse than no sheet, and
 * driving both from one source makes that impossible.
 */

const money = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
}).format(amount);

/** ✓ for included, "add-on" otherwise — the sheet's own notation. */
const Mark = ({ state }) => (
    state === 'included'
        ? <span className="mark-yes" aria-label="included">✓</span>
        : <span className="mark-addon">add-on</span>
);

const PricingSheetPage = () => {
    const packages = APP_CONFIG.packages;
    const yearlyDiscount = APP_CONFIG.billing.yearlyDiscount;
    const platformGroups = getPlatformGroups();
    // Titan OS and Desktop are quotable but not part of the published matrix.
    const publishedGroups = platformGroups.filter(g => g.includedFrom !== null);
    const extraGroups = platformGroups.filter(g => g.includedFrom === null);

    return (
        <div className="pricing-sheet-page">
            <div className="sheet-shell">
                <header className="sheet-header">
                    <span className="sheet-eyebrow">
                        <Logo height={26} />
                        <span>OTT</span>
                    </span>
                    <h1>Pricing</h1>
                    <p className="sheet-strap">
                        One platform · 4 tiers · apps &amp; features by tier · monthly, or yearly
                        (save {Math.round(yearlyDiscount * 100)}%) · no upfront
                    </p>
                    <p className="sheet-lead">
                        Everything on one plan. Pick a tier for your capacity, platforms, and features.
                        <span className="mark-yes"> ✓</span> included · <span className="mark-addon">add-on</span>{' '}
                        available for an extra monthly fee. Anything not in a tier can be added on.
                    </p>
                </header>

                {/* ---------------- The tier matrix ---------------- */}
                <div className="sheet-table-scroll">
                    <table className="sheet-matrix">
                        <thead>
                            <tr>
                                <th scope="col" className="row-head">Plan</th>
                                {TIER_ORDER.map(t => (
                                    <th scope="col" key={t} className={packages[t].popular ? 'is-popular' : ''}>
                                        <span className="tier-name">{packages[t].name}</span>
                                        {packages[t].popular && <span className="tier-flag">★ popular</span>}
                                        {packages[t].custom ? (
                                            <span className="tier-price">Contact us</span>
                                        ) : (
                                            <>
                                                <span className="tier-price">~{money(packages[t].price)}/mo</span>
                                                <span className="tier-sub">
                                                    or ~{money(Math.round(packages[t].price * 12 * (1 - yearlyDiscount)))}/yr
                                                    {' '}(−{Math.round(yearlyDiscount * 100)}%)
                                                </span>
                                            </>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            <tr className="section-row">
                                <th colSpan={TIER_ORDER.length + 1} scope="colgroup">Capacity</th>
                            </tr>
                            <tr>
                                <th scope="row" className="row-head">Bandwidth / month</th>
                                {TIER_ORDER.map(t => <td key={t} className="num">{formatCapacity(packages[t].bandwidth)}</td>)}
                            </tr>
                            <tr>
                                <th scope="row" className="row-head">Storage</th>
                                {TIER_ORDER.map(t => <td key={t} className="num">{formatCapacity(packages[t].storage)}</td>)}
                            </tr>
                            <tr>
                                <th scope="row" className="row-head">Approx. active subscribers</th>
                                {TIER_ORDER.map(t => (
                                    <td key={t} className="num">
                                        {packages[t].activeSubscribers === Infinity
                                            ? 'Custom'
                                            : `~${packages[t].activeSubscribers.toLocaleString()}`}
                                    </td>
                                ))}
                            </tr>

                            <tr className="section-row">
                                <th colSpan={TIER_ORDER.length + 1} scope="colgroup">Platforms</th>
                            </tr>
                            {publishedGroups.map(group => (
                                <tr key={group.group}>
                                    <th scope="row" className="row-head">{group.group}</th>
                                    {TIER_ORDER.map(t => (
                                        <td key={t}>
                                            <Mark state={tierAvailability(APP_CONFIG.platforms[group.keys[0]], t)} />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                            <tr className="section-row">
                                <th colSpan={TIER_ORDER.length + 1} scope="colgroup">Features</th>
                            </tr>
                            {getCoreFeatures().map(([key, feature]) => (
                                <tr key={key}>
                                    <th scope="row" className="row-head">{feature.name}</th>
                                    {TIER_ORDER.map(t => <td key={t}><Mark state="included" /></td>)}
                                </tr>
                            ))}
                            {getOptionalFeatures().map(([key, feature]) => (
                                <tr key={key}>
                                    <th scope="row" className="row-head">{feature.name}</th>
                                    {TIER_ORDER.map(t => (
                                        <td key={t}><Mark state={tierAvailability(feature, t)} /></td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className="sheet-enterprise">
                    <strong>Enterprise / unlimited:</strong> unlimited capacity, all platforms, and a
                    custom SLA — contact us for a quote.
                </p>
                <p className="sheet-footnote">
                    Approx. active subscribers is a rough guide based on typical usage; real capacity
                    varies with video quality and watch time.
                </p>

                {/* ---------------- Why this model ---------------- */}
                <section className="sheet-section sheet-rationale">
                    <ol>
                        <li>
                            <h3>Easy to understand and easy to plan</h3>
                            <p>The customer pays one price every month. That is all.</p>
                            <p>There is no fee for each user, and we do not take a share of their money.</p>
                            <p>So when they grow and get more users, their price stays the same.</p>
                            <p>This makes it easy for them to say yes, and it is a strong point in sales.</p>
                        </li>
                        <li>
                            <h3>No big payment at the start</h3>
                            <p>The apps are inside the monthly price. The customer does not pay a big amount at the start.</p>
                            <p>A big first payment is the main reason deals stop. Now that problem is gone.</p>
                            <p>
                                For us, this turns one-time app money into money every month. That means
                                steady income (MRR) and more value from each customer over time.
                            </p>
                        </li>
                        <li>
                            <h3>Easy to grow to a higher tier</h3>
                            <p>Each tier gives more platforms and more features than the one before.</p>
                            <p>So the customer has a clear reason to move up: Starter → Streamer → Content Guru → Advanced.</p>
                            <p>
                                Small customers do not need to jump to a big tier. They can just add one
                                feature as an add-on when they need it.
                            </p>
                        </li>
                    </ol>
                    <p className="sheet-inshort">
                        In short: one simple price, no big first payment, and an easy way to grow.
                    </p>
                </section>

                {/* ---------------- Platform add-ons ---------------- */}
                <section className="sheet-section">
                    <h2>Platform add-ons (monthly)</h2>
                    <p>
                        Each individual platform can be added for a flat {money(150)}/mo — the same
                        simple price for every one.
                    </p>
                    <div className="sheet-table-scroll">
                        <table className="sheet-list">
                            <thead>
                                <tr><th scope="col">Platform add-on</th><th scope="col" className="num">Monthly</th></tr>
                            </thead>
                            <tbody>
                                {publishedGroups.flatMap(g => g.keys)
                                    .map(key => [key, APP_CONFIG.platforms[key]])
                                    .filter(([, platform]) => platform.price > 0)
                                    .map(([key, platform]) => (
                                        <tr key={key}>
                                            <th scope="row">{platform.name}</th>
                                            <td className="num">{money(platform.price)} / mo</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="sheet-note-italic">
                        <strong>Upgrading usually wins:</strong> a higher tier bundles several platforms
                        with more features and capacity, so adding platforms one by one rarely beats
                        simply moving up. Extra bandwidth and storage beyond your tier are billed per GB
                        ({money(APP_CONFIG.overage.bandwidth.pricePerGB)}/GB transfer,{' '}
                        {money(APP_CONFIG.overage.storage.pricePerGB)}/GB storage).
                    </p>
                    {extraGroups.length > 0 && (
                        <p className="sheet-extra">
                            Also quotable on request, at the same {money(150)}/mo and on any tier:{' '}
                            {extraGroups.flatMap(g => g.keys).map(k => APP_CONFIG.platforms[k].name).join(', ')}.
                        </p>
                    )}
                </section>

                {/* ---------------- Feature add-ons ---------------- */}
                <section className="sheet-section">
                    <h2>Feature add-ons (monthly)</h2>
                    <p>
                        Any feature not in your tier can be added on its own. The “included from” column
                        shows the tier where it already comes bundled.
                    </p>
                    <div className="sheet-table-scroll">
                        <table className="sheet-list">
                            <thead>
                                <tr>
                                    <th scope="col">Feature add-on</th>
                                    <th scope="col">Included from</th>
                                    <th scope="col" className="num">Monthly</th>
                                </tr>
                            </thead>
                            <tbody>
                                {getOptionalFeatures().map(([key, feature]) => (
                                    <tr key={key}>
                                        <th scope="row">{feature.name}</th>
                                        <td className="from-tier">{packages[feature.includedFrom].name}</td>
                                        <td className="num">{money(feature.price)} / mo</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* ---------------- Usage-based add-ons ---------------- */}
                <section className="sheet-section">
                    <h2>Usage-based add-ons</h2>
                    <p>
                        Billed on what you use. Channel bandwidth is invoiced monthly, after the month,
                        based on actual usage.
                    </p>
                    <div className="sheet-table-scroll">
                        <table className="sheet-list">
                            <thead>
                                <tr><th scope="col">Add-on</th><th scope="col" className="num">Price</th></tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th scope="row">{APP_CONFIG.usageAddOns.ssaiChannelBuilder.name}</th>
                                    <td className="num">
                                        {money(APP_CONFIG.usageAddOns.ssaiChannelBuilder.unitPrice)} per channel / month
                                        {' + '}bandwidth billed monthly at{' '}
                                        {money(APP_CONFIG.usageAddOns.ssaiChannelBuilder.bandwidthPricePerTB)} / TB
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row">{APP_CONFIG.usageAddOns.aiDubbing.name}</th>
                                    <td className="num">
                                        {money(APP_CONFIG.usageAddOns.aiDubbing.unitPrice)} per minute
                                    </td>
                                </tr>
                                <tr>
                                    <th scope="row">{APP_CONFIG.usageAddOns.aiSubtitles.name}</th>
                                    <td className="num">
                                        {money(APP_CONFIG.usageAddOns.aiSubtitles.unitPrice)} per minute
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <footer className="sheet-foot">
                    <button type="button" className="sheet-print" onClick={() => window.print()}>
                        Print this sheet
                    </button>
                    <p>
                        Generated from the calculator's own pricing configuration, so it always matches
                        what the tool quotes.
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default PricingSheetPage;
